export type ComposerTargetKind = 'space' | 'composition';

/** The kind vocabulary `GET /composables/search` accepts/reports (composition kind ∪ catalog role). */
export type SearchableKind = 'surface' | 'section' | 'fragment' | 'primitive';

/**
 * What may legally be PLACED into a composer target, by target kind (EAINT-844 Phase F4). A
 * Space's single Surface is swapped through a dedicated topbar control, never through the
 * rail/agent search — so `'surface'` never appears here for either target. A Space accepts
 * grid-placeable modules: composition kind `'section'`, code-backed role `'section'`, and
 * composition kind `'fragment'` (placeable as a section). A composition (fragment/section
 * authoring, EAINT-844 OQ-8) accepts only primitive vocabulary plus nested fragments.
 *
 * Shared between the registry rail (design-system) and the composer agent's `search_composables`
 * tool (composer-agent-service) so a ghost the agent proposes can never violate the same grammar
 * a human placing from the rail is bound by.
 */
export const PLACEABLE_KINDS_BY_TARGET: Record<ComposerTargetKind, readonly SearchableKind[]> = {
  space: ['section', 'fragment'],
  composition: ['primitive', 'fragment'],
};

/** Whether `kind` may legally be placed into a target of `targetKind`. `null`/`undefined` (unknown kind) is never placeable. */
export function isKindPlaceableForTarget(
  kind: SearchableKind | null | undefined,
  targetKind: ComposerTargetKind
): boolean {
  if (!kind) return false;
  return PLACEABLE_KINDS_BY_TARGET[targetKind].includes(kind);
}

/**
 * Whether `kind` may be placed into ANY composer target. Only `'surface'` is placeable
 * nowhere — a Space's single Surface is swapped through the dedicated topbar control
 * (EAINT-844 Phase F4b), not placed from the rail or proposed by the agent.
 *
 * `GET /composables/search` uses this to tell a PLACEMENT query (rail, gallery, agent
 * `search_composables`) apart from the surface-swap query: only the former hides
 * `internal` catalog entries, because "internal" means "never a placement pick" — the
 * two internal Surface roots (`SpaceGrid`, `ContextShell`) are still legitimate swap
 * targets and must remain reachable, or a Space swapped off them can never return.
 */
export function isKindPlaceableAnywhere(kind: SearchableKind | null | undefined): boolean {
  if (!kind) return false;
  return Object.values(PLACEABLE_KINDS_BY_TARGET).some((kinds) => kinds.includes(kind));
}
